import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface SignedPhoto {
  key: string;
  url: string | null;
  contentType: string;
  uploadedAt: string;
}

interface UploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
}

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic']);
const MAX_BYTES = 12 * 1024 * 1024;

/**
 * Reusable gallery + uploader for entities that store photos in MinIO via
 * presigned PUT. The two endpoint paths (upload-url, confirm, list) are
 * passed in by the parent so the same component handles pallets and SKUs.
 *
 * Caller controls reload with `reloadKey` to force a refresh after another
 * action (e.g. state transition) might have invalidated cached signed URLs.
 */
export function PhotoGallery({
  basePath,
  canEdit,
  canUpload = canEdit,
  reloadKey = 0,
}: {
  /** e.g. `/pallets/<id>/photos` or `/partners/<pid>/skus/<sid>/photos` */
  basePath: string;
  canEdit: boolean;
  canUpload?: boolean;
  reloadKey?: number;
}) {
  const [photos, setPhotos] = useState<SignedPhoto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalKey, setInternalKey] = useState(0);

  useEffect(() => {
    setPhotos(null);
    api<{ photos: SignedPhoto[] }>(`${basePath}/list`, { method: 'POST' })
      .then((r) => setPhotos(r.photos))
      .catch(() => setPhotos([]));
  }, [basePath, reloadKey, internalKey]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file later
    setError(null);

    if (!ALLOWED_MIME.has(file.type)) {
      setError(t('photo.unsupported_type'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('photo.too_large'));
      return;
    }

    setBusy(true);
    try {
      // 1. Get presigned PUT URL.
      const presigned = await api<UploadUrlResponse>(`${basePath}/upload-url`, {
        method: 'POST',
        body: { contentType: file.type, sizeBytes: file.size },
      });
      // 2. Upload bytes directly to MinIO. We bypass the api fetch wrapper
      // because we don't want CSRF or credentials on this third-party PUT.
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`upload PUT failed (${putRes.status})`);
      }
      // 3. Confirm — server records into entity's photos JSONB.
      await api<{ photos: SignedPhoto[] }>(`${basePath}/confirm`, {
        method: 'POST',
        body: {
          objectKey: presigned.objectKey,
          contentType: file.type,
          sizeBytes: file.size,
        },
      });
      setInternalKey((k) => k + 1);
    } catch {
      setError(t('photo.upload_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(key: string) {
    if (!window.confirm(t('photo.confirm_delete'))) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ photos: SignedPhoto[] }>(`${basePath}/delete`, {
        method: 'POST',
        body: { objectKey: key },
      });
      setPhotos(r.photos);
    } catch {
      setError(t('photo.delete_failed'));
    } finally {
      setBusy(false);
    }
  }

  // Swap a photo with its neighbour. The first entry is the primary photo, so
  // moving one to the front sets it as primary.
  async function onMove(index: number, dir: -1 | 1) {
    if (!photos) return;
    const target = index + dir;
    if (target < 0 || target >= photos.length) return;
    const keys = photos.map((p) => p.key);
    const a = keys[index];
    const b = keys[target];
    if (a === undefined || b === undefined) return;
    keys[index] = b;
    keys[target] = a;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ photos: SignedPhoto[] }>(`${basePath}/reorder`, {
        method: 'POST',
        body: { orderedKeys: keys },
      });
      setPhotos(r.photos);
    } catch {
      setError(t('photo.reorder_failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('photo.title')}
        </h3>
        {canUpload ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              className="hidden"
              onChange={(e) => void onFile(e)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('photo.add')}
            </button>
          </>
        ) : null}
      </div>
      {error ? <div className="text-sm text-red-600">{error}</div> : null}
      {photos === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : photos.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {t('photo.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div
              key={p.key}
              className="relative rounded border border-border overflow-hidden bg-muted"
            >
              <a href={p.url ?? '#'} target="_blank" rel="noreferrer" className="block">
                {p.url ? (
                  <img
                    src={p.url}
                    alt=""
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-muted-foreground">
                    ?
                  </div>
                )}
              </a>
              {i === 0 ? (
                <span className="absolute top-0 left-0 bg-primary text-primary-foreground text-[10px] px-1 rounded-br">
                  {t('photo.primary')}
                </span>
              ) : null}
              {canEdit ? (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/55 px-1 py-0.5 text-white">
                  <div className="flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => void onMove(i, -1)}
                      disabled={busy || i === 0}
                      aria-label={t('photo.move_left')}
                      className="px-1.5 leading-none disabled:opacity-30"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      onClick={() => void onMove(i, 1)}
                      disabled={busy || i === photos.length - 1}
                      aria-label={t('photo.move_right')}
                      className="px-1.5 leading-none disabled:opacity-30"
                    >
                      →
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onDelete(p.key)}
                    disabled={busy}
                    aria-label={t('common.delete')}
                    className="px-1.5 leading-none text-red-300 disabled:opacity-30"
                  >
                    ×
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
